/**
 * Bespoke icon set — replaces the emoji icons across the customer surface
 * with a consistent themed family. All icons share:
 *   - viewBox 0 0 24 24
 *   - stroke="url(#kgdb-icon-grad)" (theme blue → purple gradient)
 *   - stroke-width 1.5, stroke-linecap/join round, fill="none"
 *
 * Mount <IconDefs /> once at the app shell so url(#kgdb-icon-grad)
 * resolves; CustomerLayout already does this. Icons fall back to
 * currentColor stroke if defs aren't mounted (so they still work in
 * isolation).
 *
 * Sizing: pass `size` (default 18). All icons line up visually at
 * matching font-size + padding because they share the 24-grid viewbox.
 */
import type { ReactNode } from 'react';

export const ICON_GRADIENT_ID = 'kgdb-icon-grad';

/** Render once at the app shell. Defines the linearGradient referenced by every icon. */
export function IconDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: 'absolute', width: 0, height: 0 }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={ICON_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4a9eff" />
          <stop offset="100%" stopColor="#9B5DE5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

interface BaseProps {
  size?: number;
  className?: string;
  title?: string;
  /** Override stroke color — useful for muted/disabled states. */
  color?: string;
}

function Icon({
  size = 18,
  className,
  title,
  color,
  children,
}: BaseProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? `url(#${ICON_GRADIENT_ID})`}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {children}
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────
// Platform tabs (5)
// ──────────────────────────────────────────────────────────────────

export function IconTv(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="2.5" y="6" width="19" height="12.5" rx="2" />
      <path d="M8 3l4 3 4-3" />
      <path d="M2.5 21h19" />
    </Icon>
  );
}

export function IconRadio(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="2.5" y="7" width="19" height="13" rx="2" />
      <circle cx="16.5" cy="13.5" r="3" />
      <path d="M6 4l13 3" />
      <path d="M6 12h5M6 15h5M6 18h5" />
    </Icon>
  );
}

export function IconSocial(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="6.5" r="2.5" />
      <circle cx="6" cy="17" r="2.5" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M12 9v3M10 14l-3 1.5M14 14l3 1.5" />
    </Icon>
  );
}

export function IconDigital(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="2.5" y="4" width="19" height="13" rx="2" />
      <path d="M9 21h6M12 17v4" />
      <path d="M8 9l3 2-3 2M13 13h4" />
    </Icon>
  );
}

export function IconOutdoor(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="3.5" width="18" height="11" rx="1.5" />
      <path d="M12 14.5V21" />
      <path d="M9 21h6" />
      <path d="M7 8h7M7 11h5" />
    </Icon>
  );
}

// ──────────────────────────────────────────────────────────────────
// Audience categories (18)
// ──────────────────────────────────────────────────────────────────

export function IconPrivateHomes(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M10 20v-5h4v5" />
    </Icon>
  );
}

export function IconLowRise(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="9" width="18" height="11" rx="1" />
      <path d="M7 13h2M11 13h2M15 13h2" />
      <path d="M7 17h2M11 17h2M15 17h2" />
      <path d="M3 9l3-3h12l3 3" />
    </Icon>
  );
}

export function IconHighRise(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M8.5 7h2M13.5 7h2M8.5 11h2M13.5 11h2M8.5 15h2M13.5 15h2" />
      <path d="M10 21v-3h4v3" />
    </Icon>
  );
}

export function IconWorkers(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M9 8V5a1 1 0 011-1h4a1 1 0 011 1v3" />
      <path d="M3 13h18" />
      <path d="M11 12.5h2v2h-2z" />
    </Icon>
  );
}

export function IconUniversity(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M2 9l10-4 10 4-10 4-10-4z" />
      <path d="M6 11v5c0 1 2.5 3 6 3s6-2 6-3v-5" />
      <path d="M22 9v5" />
    </Icon>
  );
}

export function IconSchoolchildren(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 9V7a2 2 0 012-2h10a2 2 0 012 2v2" />
      <rect x="4" y="9" width="16" height="11" rx="2" />
      <path d="M9 5V3.5M15 5V3.5" />
      <path d="M9 14h6" />
    </Icon>
  );
}

export function IconPreschoolParents(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="9" cy="6" r="2.5" />
      <path d="M5 19v-3a4 4 0 014-4 4 4 0 014 4v3" />
      <circle cx="17" cy="9" r="1.8" />
      <path d="M14 19v-2a3 3 0 013-3 3 3 0 013 3v2" />
    </Icon>
  );
}

export function IconDailyShoppers(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 8h16l-1.5 10a2 2 0 01-2 1.7H7.5a2 2 0 01-2-1.7L4 8z" />
      <path d="M8 8V5a4 4 0 018 0v3" />
    </Icon>
  );
}

export function IconMallGoers(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 7h14l-1 13H6L5 7z" />
      <path d="M9 7V5.5a3 3 0 016 0V7" />
      <path d="M9 11v2M15 11v2" />
    </Icon>
  );
}

export function IconPharmacy(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="2.5" y="9" width="19" height="6.5" rx="3.25" transform="rotate(-30 12 12.25)" />
      <path d="M8.5 7.4l7 4.05" />
    </Icon>
  );
}

export function IconPatients(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 4v6a4 4 0 008 0V4" />
      <path d="M5 4h2M11 4h2" />
      <path d="M9 14v2a4 4 0 008 0v-1" />
      <circle cx="17" cy="13" r="2" />
    </Icon>
  );
}

export function IconBeauty(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="9" y="3" width="6" height="6" rx="1" />
      <rect x="8" y="9" width="8" height="3" rx="0.5" />
      <path d="M9.5 12v8a1.5 1.5 0 003 0v-8M11 12v8" />
      <path d="M14.5 12v8a1.5 1.5 0 003 0v-8" />
    </Icon>
  );
}

export function IconFitness(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3 9v6M21 9v6" />
      <rect x="5" y="6" width="3" height="12" rx="0.5" />
      <rect x="16" y="6" width="3" height="12" rx="0.5" />
      <path d="M8 12h8" />
    </Icon>
  );
}

export function IconNightlife(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M19 13.5A7 7 0 1110.5 5a5.5 5.5 0 008.5 8.5z" />
      <path d="M16 4l.7 1.5L18 6l-1.3.5L16 8l-.7-1.5L14 6l1.3-.5z" />
    </Icon>
  );
}

export function IconFoodies(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M7 3v8a3 3 0 003 3v7" />
      <path d="M11 3v8" />
      <path d="M7 7h4" />
      <path d="M16 3c-2 1-3 3-3 5.5 0 1.5 1 2.5 3 2.5v10" />
    </Icon>
  );
}

export function IconCarOwners(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3 14l2-5a2 2 0 012-1.5h10a2 2 0 012 1.5l2 5" />
      <rect x="3" y="14" width="18" height="5" rx="1.5" />
      <circle cx="7" cy="19" r="1.5" />
      <circle cx="17" cy="19" r="1.5" />
    </Icon>
  );
}

export function IconTourists(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="4" y="7" width="16" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
      <path d="M4 13h16" />
      <path d="M9 16v2M15 16v2" />
    </Icon>
  );
}

export function IconParksPublic(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 3a5 5 0 015 5 5 5 0 01-1.5 3.5A4.5 4.5 0 0112 16a4.5 4.5 0 01-3.5-4.5A5 5 0 017 8a5 5 0 015-5z" />
      <path d="M12 16v5" />
      <path d="M9 21h6" />
    </Icon>
  );
}

// ──────────────────────────────────────────────────────────────────
// Common UI (2 used in Brief; easy to extend)
// ──────────────────────────────────────────────────────────────────

export function IconTarget(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5.5" />
      <circle cx="12" cy="12" r="2" />
    </Icon>
  );
}

export function IconSparkle(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
      <path d="M5.5 5.5l2 2M16.5 16.5l2 2M5.5 18.5l2-2M16.5 7.5l2-2" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

// ── Collaboration graph (creator card) ─────────────────────────────
export function IconCreatorNode(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="8.3" r="3.3" />
      <path d="M5.6 19.4c0-3.5 2.9-5.5 6.4-5.5s6.4 2 6.4 5.5" />
    </Icon>
  );
}

export function IconBrandNode(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 9.4 5.5 5h13L20 9.4" />
      <path d="M4 9.4h16" />
      <path d="M5.6 9.4V19h12.8V9.4" />
      <path d="M10 19v-4.6h4V19" />
    </Icon>
  );
}

export function IconCoPublish(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="8.6" cy="9" r="2.5" />
      <circle cx="15.4" cy="9" r="2.5" />
      <path d="M4.2 18c0-2.5 1.9-3.9 4.4-3.9 1 0 1.9.2 2.6.7" />
      <path d="M19.8 18c0-2.5-1.9-3.9-4.4-3.9-1 0-1.9.2-2.6.7" />
    </Icon>
  );
}

export function IconReshare(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 8.5h11a3 3 0 0 1 3 3v.8" />
      <path d="M7 5.5 4 8.5l3 3" />
      <path d="M20 15.5H9a3 3 0 0 1-3-3v-.8" />
      <path d="M17 18.5l3-3-3-3" />
    </Icon>
  );
}

export function IconTag(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3.6 11.4 11.4 3.6a2 2 0 0 1 1.4-.6H19a2 2 0 0 1 2 2v6.2a2 2 0 0 1-.6 1.4l-7.8 7.8a2 2 0 0 1-2.8 0l-6.2-6.2a2 2 0 0 1 0-2.8Z" />
      <circle cx="16.4" cy="7.6" r="1.1" />
    </Icon>
  );
}

export function IconMention(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M15.1 12v1.7a2.4 2.4 0 0 0 4.8 0V12a8 8 0 1 0-3.2 6.4" />
    </Icon>
  );
}

// ──────────────────────────────────────────────────────────────────
// Extended UI set — replaces decorative emoji across the app
// ──────────────────────────────────────────────────────────────────

export function IconTrophy(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M7 4h10v5a5 5 0 01-10 0V4z" />
      <path d="M7 6H4.5a2 2 0 002 3M17 6h2.5a2 2 0 01-2 3" />
      <path d="M12 14v3M9 20h6M10 20v-3h4v3" />
    </Icon>
  );
}

export function IconStar(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.25 4.1 1 5.85L12 16.9l-5.25 2.75 1-5.85L3.5 9.7l5.9-.9z" />
    </Icon>
  );
}

export function IconGift(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3.5" y="8.5" width="17" height="4" rx="1" />
      <path d="M5 12.5V20h14v-7.5M12 8.5V20" />
      <path d="M12 8.5C9 8.5 7 7.5 7 6a2 2 0 014 0c0 1.5 1 2.5 1 2.5zM12 8.5c3 0 5-1 5-2.5a2 2 0 00-4 0c0 1.5-1 2.5-1 2.5z" />
    </Icon>
  );
}

export function IconMoney(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.8" />
      <path d="M5.5 9v6M18.5 9v6" />
    </Icon>
  );
}

export function IconCamera(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3 8.5h3l1.5-2h9L18 8.5h3a0 0 0 010 0v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9z" />
      <circle cx="12" cy="12.5" r="3.2" />
    </Icon>
  );
}

export function IconImage(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.6" />
      <path d="M4 17l4.5-4.5 3.5 3.5 3-3 5 5" />
    </Icon>
  );
}

export function IconFilm(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M3 9h18M3 15h18M8 4.5v15M16 4.5v15" />
    </Icon>
  );
}

export function IconMusic(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </Icon>
  );
}

export function IconPlay(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M8 5.5l10 6.5-10 6.5z" />
    </Icon>
  );
}

export function IconSend(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M21 3L3 10.5l7 2.5 2.5 7L21 3z" />
      <path d="M10 13l11-10" />
    </Icon>
  );
}

export function IconNews(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 5h13v14H6a2 2 0 01-2-2V5z" />
      <path d="M17 8h3v9a2 2 0 01-2 2" />
      <path d="M7 8h7M7 11h7M7 14h4" />
    </Icon>
  );
}

export function IconChat(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 5.5h16a0 0 0 010 0v9a2 2 0 01-2 2H9l-4 3.5v-3.5a1 1 0 01-1-1v-10z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </Icon>
  );
}

export function IconMegaphone(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 10v4a1 1 0 001 1h2l9 4V5L7 9H5a1 1 0 00-1 1z" />
      <path d="M8 15v3.5a1.5 1.5 0 003 0V16" />
      <path d="M19 9a3 3 0 010 6" />
    </Icon>
  );
}

export function IconChartUp(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 4v16h16" />
      <path d="M7 14l3.5-3.5 3 3L20 7" />
      <path d="M20 11V7h-4" />
    </Icon>
  );
}

export function IconChartDown(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 4v16h16" />
      <path d="M7 8l3.5 3.5 3-3L20 15" />
      <path d="M20 11v4h-4" />
    </Icon>
  );
}

export function IconChartBars(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 4v16h16" />
      <rect x="7" y="11" width="3" height="6" rx="0.5" />
      <rect x="13" y="7" width="3" height="10" rx="0.5" />
    </Icon>
  );
}

export function IconClock(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  );
}

export function IconEye(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </Icon>
  );
}

export function IconHome(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 11 12 4l8 7" />
      <path d="M6.5 9.5V19h11V9.5" />
      <path d="M10 19v-4.5h4V19" />
    </Icon>
  );
}

export function IconUser(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
    </Icon>
  );
}

export function IconHeart(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 20s-7-4.35-9.2-8.4C1.2 8.7 2.6 5.5 5.7 5.5c1.9 0 3.2 1.1 4.3 2.6 1.1-1.5 2.4-2.6 4.3-2.6 3.1 0 4.5 3.2 2.9 6.1C19 15.65 12 20 12 20z" />
    </Icon>
  );
}

export function IconSearch(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5L21 21" />
    </Icon>
  );
}

export function IconPin(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 21s6.5-5.7 6.5-10.5A6.5 6.5 0 005.5 10.5C5.5 15.3 12 21 12 21z" />
      <circle cx="12" cy="10.5" r="2.5" />
    </Icon>
  );
}

export function IconLink(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M9.5 14.5l5-5" />
      <path d="M8 11l-2 2a3 3 0 004.2 4.2l2-2" />
      <path d="M16 13l2-2a3 3 0 00-4.2-4.2l-2 2" />
    </Icon>
  );
}

export function IconDiamond(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M6 4h12l3 5-9 11L3 9z" />
      <path d="M3 9h18M9 4l-3 5 6 11 6-11-3-5" />
    </Icon>
  );
}

export function IconWarning(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 4l9 15.5H3z" />
      <path d="M12 10v4.5M12 17.2v.1" />
    </Icon>
  );
}

export function IconQuestion(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.5 9.5a2.5 2.5 0 014.8.9c0 1.7-2.3 2.1-2.3 3.6M12 17.2v.1" />
    </Icon>
  );
}

export function IconMap(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M9 4L3.5 6v14L9 18l6 2 5.5-2V4L15 6 9 4z" />
      <path d="M9 4v14M15 6v14" />
    </Icon>
  );
}

export function IconPencil(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M14.5 5.5l4 4M4 20l1-4L16 5a2 2 0 013 3L8 19l-4 1z" />
    </Icon>
  );
}

export function IconCart(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M3 4h2l2 11h11l2-7H6.5" />
      <circle cx="9" cy="19" r="1.5" />
      <circle cx="17" cy="19" r="1.5" />
    </Icon>
  );
}

export function IconEmail(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M4 7l8 6 8-6" />
    </Icon>
  );
}

export function IconList(p: BaseProps) {
  return (
    <Icon {...p}>
      <rect x="6" y="4" width="13" height="17" rx="2" />
      <path d="M9 4.5h7V3a1 1 0 00-1-1h-5a1 1 0 00-1 1v1.5z" />
      <path d="M9 11h7M9 15h5" />
    </Icon>
  );
}

export function IconSave(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M8 4v5h7V4M8 20v-6h8v6" />
    </Icon>
  );
}

export function IconBook(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 4.5h11a2 2 0 012 2V20H7a2 2 0 01-2-2V4.5z" />
      <path d="M5 16.5h13" />
    </Icon>
  );
}

export function IconCheck(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Icon>
  );
}

export function IconX(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  );
}

export function IconArrowRight(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 12h15M13 6l6 6-6 6" />
    </Icon>
  );
}

export function IconArrowLeft(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M20 12H5M11 6l-6 6 6 6" />
    </Icon>
  );
}

export function IconArrowUp(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 20V5M6 11l6-6 6 6" />
    </Icon>
  );
}

export function IconArrowDown(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M12 4v15M6 13l6 6 6-6" />
    </Icon>
  );
}

export function IconArrowUpRight(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M7 17L17 7M8 7h9v9" />
    </Icon>
  );
}

export function IconSwap(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 8h13M14 5l3 3-3 3" />
      <path d="M20 16H7M10 13l-3 3 3 3" />
    </Icon>
  );
}

export function IconRefresh(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M20 11a8 8 0 10-2 6" />
      <path d="M20 5v6h-6" />
    </Icon>
  );
}

export function IconInfo(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <path d="M12 8v.1" />
    </Icon>
  );
}

export function IconTrash(p: BaseProps) {
  return (
    <Icon {...p}>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" />
      <path d="M6 7l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13" />
      <path d="M10 11v6M14 11v6" />
    </Icon>
  );
}

export function IconGear(p: BaseProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </Icon>
  );
}

// Small lookup so callers can render `<NamedIcon name="parks" size={14}/>` from a string.
export const ICONS_BY_KEY: Record<string, (p: BaseProps) => JSX.Element> = {
  // platforms
  tv: IconTv,
  radio: IconRadio,
  social: IconSocial,
  digital: IconDigital,
  outdoor: IconOutdoor,
  // audience
  private_homes: IconPrivateHomes,
  low_rise: IconLowRise,
  high_rise: IconHighRise,
  workers: IconWorkers,
  university: IconUniversity,
  schoolchildren: IconSchoolchildren,
  preschool_parents: IconPreschoolParents,
  daily_shoppers: IconDailyShoppers,
  mall_goers: IconMallGoers,
  pharmacy: IconPharmacy,
  patients: IconPatients,
  beauty: IconBeauty,
  fitness: IconFitness,
  nightlife: IconNightlife,
  foodies: IconFoodies,
  car_owners: IconCarOwners,
  tourists: IconTourists,
  parks: IconParksPublic,
  // common
  target: IconTarget,
  sparkle: IconSparkle,
  // collaboration graph
  creator_node: IconCreatorNode,
  brand_node: IconBrandNode,
  copublish: IconCoPublish,
  reshare: IconReshare,
  collab_tag: IconTag,
  mention: IconMention,
  // extended UI set
  trophy: IconTrophy,
  star: IconStar,
  gift: IconGift,
  money: IconMoney,
  camera: IconCamera,
  image: IconImage,
  film: IconFilm,
  music: IconMusic,
  play: IconPlay,
  send: IconSend,
  news: IconNews,
  chat: IconChat,
  megaphone: IconMegaphone,
  chart_up: IconChartUp,
  chart_down: IconChartDown,
  chart_bars: IconChartBars,
  clock: IconClock,
  eye: IconEye,
  home: IconHome,
  user: IconUser,
  heart: IconHeart,
  search: IconSearch,
  pin: IconPin,
  link: IconLink,
  diamond: IconDiamond,
  warning: IconWarning,
  question: IconQuestion,
  map: IconMap,
  pencil: IconPencil,
  cart: IconCart,
  email: IconEmail,
  list: IconList,
  save: IconSave,
  book: IconBook,
  check: IconCheck,
  x: IconX,
  arrow_right: IconArrowRight,
  arrow_left: IconArrowLeft,
  arrow_up: IconArrowUp,
  arrow_down: IconArrowDown,
  arrow_up_right: IconArrowUpRight,
  swap: IconSwap,
  refresh: IconRefresh,
  info: IconInfo,
  trash: IconTrash,
  gear: IconGear,
  // aliases → reuse existing glyphs
  handshake: IconCoPublish,
  monitor: IconDigital,
  phone: IconDigital,
};

export function NamedIcon({ name, ...rest }: BaseProps & { name: string }) {
  const C = ICONS_BY_KEY[name];
  if (!C) return null;
  return <C {...rest} />;
}
